import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { ApiError, apiUrl } from '../api';

type Mode = 'login' | 'register';

// Email+password sign-in and registration, plus a Google button that does a
// full-page redirect into the Worker's OAuth flow.
export function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'register') await register(email, password, displayName || undefined);
      else await login(email, password);
      navigate('/');
    } catch (err) {
      setError(messageFor(err, mode));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hero">
      <h1>{mode === 'login' ? 'Sign in' : 'Create account'}</h1>
      <p className="lede">Your library and reading progress, synced across devices.</p>

      <form className="card form" onSubmit={submit}>
        {mode === 'register' && (
          <label>
            Name <span className="muted">(optional)</span>
            <input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoComplete="name" />
          </label>
        )}
        <label>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
            minLength={8}
            required
          />
        </label>

        {error && <p className="err">{error}</p>}

        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create account'}
        </button>

        <div className="divider"><span>or</span></div>

        <a className="btn btn-google" href={apiUrl('/api/auth/google/start')}>
          Continue with Google
        </a>
      </form>

      <p className="switch">
        {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
        <button
          type="button"
          className="linkbtn"
          onClick={() => {
            setMode(mode === 'login' ? 'register' : 'login');
            setError(null);
          }}
        >
          {mode === 'login' ? 'Create one' : 'Sign in'}
        </button>
      </p>
    </section>
  );
}

function messageFor(err: unknown, mode: Mode): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Email or password is incorrect.';
    if (err.status === 409) return 'That email is already registered.';
    if (err.status === 400) return 'Enter a valid email and a password of at least 8 characters.';
    if (err.status === 0 || err.status >= 500) return 'Server unavailable. Is the API running?';
  }
  return mode === 'register' ? 'Could not create the account.' : 'Could not sign in.';
}
